/**
 * Доменные типы агента «ОСНОВА.Руководитель» (`agent_code = progress`).
 *
 * Зеркало DTO роутов `/api/v1/agents/progress/*`, snake_case 1:1 с backend serde.
 * Агент собирает сводный отчёт о ходе строительства по объекту: KPI план/факт из
 * первичных источников (контрактная смета, реестр КС, протоколы совещаний),
 * пояснительную записку (narrative) и предупреждения о нехватке данных. Результат
 * проходит HITL-ревью на уровне прогона (принять / отклонить / оспорить) и
 * выгружается в DOCX.
 */

import { useMeQuery } from '@shared/api';

/** Тон Badge — тот же набор значений, что принимает UI-примитив `@shared/ui/Badge`
 *  (как в `@entities/mto/types`). */
export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/* ── Карточка агента и метрики (витрина) ───────────────────────────────────── */

/** Статус модуля агента. На каркасе модули в разработке (`planned`); union держим
 *  открытым к будущим состояниям (`active`). */
export type ProgressModuleStatus = 'planned' | 'active';

/** Один модуль агента (витрина в дашборде). */
export interface ProgressModule {
  /** Код модуля (стабильный идентификатор, латиница). */
  code: string;
  /** Текущий статус модуля. */
  status: ProgressModuleStatus;
}

/** Карточка агента (`/info`): код агента + перечень модулей. */
export interface ProgressInfo {
  agent_code: string;
  modules: ProgressModule[];
}

/** Метрики агента (`/metrics`). На каркасе — нули. */
export interface ProgressMetrics {
  /** Всего сформированных отчётов (прогонов). */
  runs_total: number;
  /** Завершённых отчётов. */
  completed_total: number;
  /** В очереди на сборку (`status = 'created'`): задача ещё не взята воркером. */
  queued: number;
  /** В процессе формирования (`status = 'processing'`). */
  in_progress: number;
}

/* ── Область отчёта (scope) ────────────────────────────────────────────────── */

/**
 * Область формирования отчёта. `project_id` обязателен — это объект (проект ПТО),
 * по которому собирается сводка. Остальные привязки опциональны: контрактная смета
 * (источник плана), серия совещаний (источник статусов/поручений) и «человеческое»
 * имя объекта из реестра (если у проекта нет привязки к ПТО).
 */
export interface ProgressScope {
  ksp_include?: boolean;
  /** Объект (проект), по которому собирается отчёт. Обязателен. */
  project_id: string;
  /** Контрактная смета — источник плановых показателей, либо `null`. */
  contract_estimate_id?: string | null;
  /** Серия совещаний — источник статусов и поручений, либо `null`. */
  meetings_series_id?: string | null;
  /** Имя объекта из реестра (если задаётся вручную), либо `null`. */
  register_object_name?: string | null;
}

/* ── KPI и снимок отчёта (snapshot) ────────────────────────────────────────── */

/**
 * Один показатель сводного отчёта. Может быть план/факт-показателем
 * (`value_plan`/`value_fact` + `delta`/`delta_pct`) либо счётчиком (`count`,
 * например «открытых поручений»). `source_ref` и `inputs` дают прослеживаемость —
 * откуда взялось значение; `formula_note` поясняет расчёт; `needs_review` помечает
 * показатель, требующий ручной проверки (подсвечиваем в таблице).
 */
export interface ProgressKpi {
  /** Код показателя (стабильный идентификатор). */
  code: string;
  /** Человекочитаемое название показателя. */
  title: string;
  /** Группа показателя (для секционирования таблицы: «Сроки», «Бюджет» и т.п.). */
  group: string;
  /** Плановое значение, либо `null`. */
  value_plan?: number | null;
  /** Фактическое значение, либо `null`. */
  value_fact?: number | null;
  /** Единица измерения (`%`, `дн.`, `₽`, …), либо `null`. */
  unit?: string | null;
  /** Отклонение факта от плана (абсолютное), либо `null`. */
  delta?: number | null;
  /** Отклонение факта от плана в процентах (−36% = отставание), либо `null`. */
  delta_pct?: number | null;
  /** Процент освоения (факт/план·100, готовое число от backend), либо `null`. */
  completion_pct?: number | null;
  /** Счётчик (для показателей-количеств, без плана/факта), либо `null`. */
  count?: number | null;
  /** Ссылка на источник (документ/лист/строка). */
  source_ref: string;
  /** Перечень входных данных, участвовавших в расчёте. */
  inputs: string[];
  /** Пояснение к расчёту показателя. */
  formula_note: string;
  /** Показатель требует ручной проверки. */
  needs_review: boolean;
  /** Дополнительная заметка по показателю, либо `null`. */
  note?: string | null;
}

/**
 * Выявленное отклонение по ходу строительства (функция Ф-3). Детектор отбирает
 * значимые отклонения детерминированно из снимка (числа из KPI, не пересчёт);
 * `explanation` — осторожная гипотеза LLM (причину не выдумывает), `needs_review`
 * всегда `true` — пояснение совещательное, проходит HITL. Зеркало backend serde.
 */
export interface ProgressDeviation {
  /** Машинный код отклонения (`dev_pto_volume_lag` …). */
  code: string;
  /** Тип: `lag` | `quality` | `overdue` | `anomaly` | `backlog`. */
  kind: string;
  /** Важность: `critical` | `major` | `minor`. */
  severity: string;
  /** Человекочитаемое название отклонения. */
  title: string;
  /** Коды KPI, на которых построено отклонение (provenance). */
  related_kpi_codes: string[];
  /** Сопутствующие ненулевые сигналы той же области (контекст гипотезы). */
  related_signals: string[];
  /** Метрика (delta_pct или количество) — из KPI, не пересчитана. */
  metric?: number | null;
  /** Единица метрики (`%` | `шт`), либо `null`. */
  unit?: string | null;
  /** Источник связанного KPI. */
  source_ref: string;
  /** Пояснение-гипотеза от LLM, либо `null` (не сформировано). */
  explanation?: string | null;
  /** Пояснение требует проверки руководителем (HITL). */
  needs_review: boolean;
}

/** Снимок отчёта: область + рассчитанные KPI + отклонения + предупреждения. */
export interface ProgressSnapshot {
  ksp?: {
    frozen_at: string;
    snapshot: import('@entities/ksp/types').KspPublishedSnapshot | null;
    operational: import('@entities/ksp/types').KspSummaryEnvelope['operational'] | null;
    unavailable_reason: string | null;
  } | null;
  run_id: string;
  scope: ProgressScope;
  /** Имя объекта (резолвленное backend по `project_id`), либо `null`. */
  project_name?: string | null;
  /** Момент формирования снимка (ISO). */
  generated_at: string;
  kpis: ProgressKpi[];
  /** Выявленные отклонения с пояснениями (Ф-3). Пусто, если значимых нет. */
  deviations?: ProgressDeviation[];
  /** Предупреждения: чего не хватило для расчёта (нет КС, нет совещаний и т.п.). */
  warnings: string[];
}

/* ── Прогон (run) ──────────────────────────────────────────────────────────── */

/** Статус прогона. `created`/`processing` — активные (имеет смысл polling). */
export type ProgressRunStatus = 'created' | 'processing' | 'completed' | 'failed';

/** Решение HITL по прогону: принято / отклонено / оспорено. */
export type ProgressRunDecision = 'accepted' | 'rejected' | 'disputed';

/** Прогон формирования сводного отчёта. `snapshot`/`narrative` появляются по
 *  завершении; `progress_percent` — метаданные пайплайна 0..100. */
export interface ProgressRun {
  id: string;
  user_id?: string | null;
  scope: ProgressScope;
  status: ProgressRunStatus;
  /** Прогресс пайплайна 0..100 (метаданные, не предметное число). */
  progress_percent: number;
  /** Снимок с рассчитанными KPI (по завершении), либо `null`. */
  snapshot?: ProgressSnapshot | null;
  /** Пояснительная записка (текст LLM), либо `null`. */
  narrative?: string | null;
  /** Решение HITL по прогону, либо `null`. */
  decision?: ProgressRunDecision | null;
  decided_by?: string | null;
  decided_at?: string | null;
  /** Краткая сводка прогона (для заголовка карточки), либо `null`. */
  summary?: string | null;
  error_message?: string | null;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
}

/* ── Опции области (`/scope/options`) ──────────────────────────────────────── */

/** Опция проекта в пикере области отчёта. */
export interface ProgressProjectOption {
  id: string;
  name: string;
}

/** Опция сметы/серии в пикере области отчёта (подпись в поле `title`). */
export interface ProgressTitledOption {
  id: string;
  title: string;
}

/** Опции для формы запуска отчёта: проекты (обязательны) + опц. сметы и серии. */
export interface ProgressScopeOptions {
  projects: ProgressProjectOption[];
  estimates?: ProgressTitledOption[];
  series?: ProgressTitledOption[];
}

/* ── RBAC ──────────────────────────────────────────────────────────────────── */

/**
 * RBAC-хук агента `progress`. Зеркалит `useAgentPermission` из `@entities/user`,
 * но не расширяет его узкий union `AgentCode` (progress туда не входит — как и
 * `econ`) — читаем `/auth/me` напрямую. Безопасный дефолт: пока права не
 * загрузились, оба флага false (лучше задизейблить кнопку, чем словить 403).
 */
export function useProgressPermission(): { can_read: boolean; can_write: boolean } {
  const { data } = useMeQuery();
  const p = data?.agents.find((a) => a.agent_code === 'progress');
  return { can_read: !!p?.can_read, can_write: !!p?.can_write };
}

/* ── Хелперы статусов / тонов / подписей (по образцу `@entities/mto/types`) ─── */

/** Статус прогона активный (имеет смысл polling)? */
export function isProgressRunActive(status: string): boolean {
  return status === 'created' || status === 'processing';
}

/** Тон Badge для статуса прогона. */
export function statusTone(status: string): BadgeTone {
  switch (status) {
    case 'completed':
      return 'success';
    case 'processing':
      return 'info';
    case 'created':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Человекочитаемая подпись статуса прогона. */
export function statusLabel(status: string): string {
  switch (status) {
    case 'completed':
      return 'готов';
    case 'processing':
      return 'формируется…';
    case 'created':
      return 'в очереди';
    case 'failed':
      return 'ошибка';
    default:
      return status;
  }
}

/** Тон Badge для решения HITL по прогону. */
export function decisionTone(decision: string): BadgeTone {
  switch (decision) {
    case 'accepted':
      return 'success';
    case 'rejected':
      return 'neutral';
    case 'disputed':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** Человекочитаемая подпись решения HITL по прогону. */
export function decisionLabel(decision: string): string {
  switch (decision) {
    case 'accepted':
      return 'утверждён';
    case 'rejected':
      return 'отклонён';
    case 'disputed':
      return 'оспорен';
    default:
      return decision;
  }
}

/** Тон Badge для важности отклонения (Ф-3). */
export function deviationSeverityTone(severity: string): BadgeTone {
  switch (severity) {
    case 'critical':
      return 'danger';
    case 'major':
      return 'warning';
    case 'minor':
      return 'info';
    default:
      return 'neutral';
  }
}

/** Человекочитаемая подпись важности отклонения. */
export function deviationSeverityLabel(severity: string): string {
  switch (severity) {
    case 'critical':
      return 'критично';
    case 'major':
      return 'существенно';
    case 'minor':
      return 'незначительно';
    default:
      return severity;
  }
}

/** Подпись этапа прогона для полосы прогресса (эвристика по проценту — точного
 *  stage-кода в контракте нет). Терминальные статусы — свой текст. */
export function runStageLabel(status: string, progressPercent: number): string {
  if (status === 'created') return 'В очереди на формирование';
  if (status === 'failed') return 'Формирование прервано';
  if (status === 'completed') return 'Отчёт сформирован';
  const p = Math.max(0, Math.min(100, progressPercent));
  if (p < 5) return 'Подготовка данных';
  if (p < 40) return 'Сбор показателей по источникам';
  if (p < 70) return 'Расчёт показателей (KPI)';
  if (p < 90) return 'Формирование пояснительной записки';
  return 'Завершение формирования';
}

/** Заголовок объекта прогона: имя из снимка → имя из реестра → идентификатор
 *  проекта (резолва имени на витрине списка может не быть). */
export function runObjectLabel(run: ProgressRun): string {
  return (
    run.snapshot?.project_name?.trim() ||
    run.scope.register_object_name?.trim() ||
    run.scope.project_id
  );
}

/* ── Форматирование ────────────────────────────────────────────────────────── */

/** Число → `1 234,5` (ru-RU, до 2 знаков). `null`/`NaN` → прочерк. */
export function fmtNum(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return value.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

/** Отклонение со знаком: `+12,4` / `-3` / прочерк. */
export function fmtDelta(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}`;
}

/** Отклонение в процентах со знаком: `+8,3%` / `-1,5%` / прочерк. */
export function fmtPct(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`;
}

/** Дата-время `YYYY-MM-DDTHH:mm:ss` → `16.06.2026, 14:30`. Невалидное — как есть. */
export function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Дата `YYYY-MM-DD` → `16.06.2026`. `null`/невалидное — прочерк/как есть. */
export function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Сообщение ошибки из тела ответа API (`{"error":{"code","message"}}`). Backend
 * раздела отдаёт внятные русские причины (409 при попытке решения, «нет данных по
 * объекту» и т.п.), которые нельзя глотать общей фразой. `null`, если тело не в
 * этом формате.
 */
export function apiErrorMessage(err: unknown): string | null {
  if (typeof err !== 'object' || err === null || !('data' in err)) return null;
  const data = (err as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null || !('error' in data)) return null;
  const e = (data as { error?: unknown }).error;
  if (typeof e !== 'object' || e === null || !('message' in e)) return null;
  const m = (e as { message?: unknown }).message;
  return typeof m === 'string' && m.trim() ? m : null;
}
